import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Seo from '~components/Seo'
import Section from '~components/Section'
import DataViewer from '~utils/DataViewer'
import RenderModule from '~components/RenderModule'
import { motion, useAnimation, AnimateSharedLayout } from 'framer-motion'
import resolveLink from '~utils/resolveLink'
import Num from '~components/Num'
import BlockContent from '@sanity/block-content-to-react'
import TileProduct from '~components/TileProduct'
import { JNLetters, Tick, Plus } from '~components/Svg'
import useBreakpoint from '~utils/useBreakpoint'
import AnimatedTabButton from '~components/AnimatedTabButton'
import SlideDown from 'react-slidedown'
import useWindow from '~utils/useWindow'
import FadeTransition from '~components/FadeTransition'
import { useRect } from 'react-use-rect'
import { 
	mobile, 
	tablet, 
	desktop,
	mobileFilter,
	OptionSummaryButton,
	VariantIcon,
	NavBar,
	NavButton,
	NavHeading,
	TopicsIcon,
	NavLinks,
	NavLink,
	OrderBy,
	SortDropDown,
	Sort,
	SortButton,
	MobileDropDown,
	MobileNav,
	DownArrow,
} from '~styles/global'

const Category = ({ data, pageContext }) => {
	const page = data.indexPage ?? data.categoryPage
	const products = (data.allProducts ?? data.categoryProducts)
	// const vs = products.nodes.map(product => product.content.variants).flat()
	// vs.sort((a, b) => b.content.shopify.price - a.content.shopify.price)
	const [variants, setVariants] = useState(products.nodes.map(product => product.content.variants).flat())
	const [currentTab, setCurrentTab] = useState()
	const [gridVisible, setGridVisible] = useState(false)
	const [navOpen, setNavOpen] = useState(false)
	const [sortOpen, setSortOpen] = useState(false)
	const { title, content } = page
	const controls = useAnimation()
	const { isTablet, isMobile, isMobileFilter } = useBreakpoint()
	const window = useWindow()
	const [init, setInit] = useState(false)

	const sorts = ['Featured', 'Newest', 'Price (Lowest)', 'Price (Highest)']
	const [currentSort, setCurrentSort] = useState(sorts[0])

	const [filters, setFilters] = useState(page?.content?.filterOptions.map(option => ({
		title: option.title,
		tags: option.tags.map(tag => tag.title)
	})	
	))

	const isCurrent = page => 
		resolveLink(page).replace(/\/$/, '') === window?.location.pathname.replace(/\/$/, '')

	const toPage = (page, i, resetScroll) => {
		if(isCurrent(page)) return null
		setCurrentTab(i)
		controls.start('hidden')
		window.setTimeout(() => navigate(resolveLink(page), { state: { disableScrollUpdate: true }}), 600)
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth'
		})
	}

	const findCurrentIndex = () => {
		const tabs = [
			{slug: { current: 'store' }},
			...data?.nav?.indexTabs
		]
		return tabs.findIndex(tab => {
			return tab.slug?.current === pageContext.slug
		})
	}

	useEffect(() => {
		controls.start('visible')
		setCurrentTab(findCurrentIndex())
		if(!content?.flexibleContent?.length){
			setGridVisible(true)
		}
	}, [])

	const order = vsArg => {
		if (!vsArg) return
		let vs = [...vsArg]
		if(currentSort === 'Featured') vs.sort((a,b) => a.content.shopify.price - b.content.shopify.price).sort((a,b) => b.featured - a.featured)
		if(currentSort === 'Price (Lowest)') vs.sort((a,b) => a.content.shopify.price - b.content.shopify.price)
		if(currentSort === 'Price (Highest)') vs.sort((a,b) => b.content.shopify.price - a.content.shopify.price)
		if(currentSort === 'Newest first') vs.sort((a,b) => a.unixDate - b.unixDate)
		if(currentSort === 'Oldest first') vs.sort((a,b) => b.unixDate - a.unixDate)
		return vs
	}

	useEffect(() => {
		if(init){
			// setSortOpen(false)
			setGridVisible(true)
			setVariants(order(variants))
			// window.scrollTo({
			// 	top: 0,
			// 	left: 0,
			// 	behavior: 'smooth'
			// })
		} else {
			setVariants(order(variants))
			setInit(true)
		}
	}, [currentSort, filters])

	const toggleNavSort = ()=> {
		if (navOpen){
			setNavOpen(false)
			setSortOpen(!sortOpen)
		} else {
			setSortOpen(!sortOpen)
		}
	}

	const toggleSortNav = ()=> {
		if (sortOpen){
			setSortOpen(false)
			setNavOpen(!navOpen)
		} else {
			setNavOpen(!navOpen)
		}
	}

	const selectTag = (tag, optionTitle) => {
		const optionIndex = filters.findIndex(o => o.title === optionTitle)
		setGridVisible(true)
		let newFilters = [...filters]
		if(tag === 'all'){
			newFilters[optionIndex] = {
				title: page?.content?.filterOptions[optionIndex].title,
				tags: page?.content?.filterOptions[optionIndex].tags.map(tag => tag.title)
			}
		} else {
			newFilters[optionIndex] = {
				...newFilters[optionIndex],
				tags: [tag]
			}
		}
		setFilters(newFilters)
	}

	const [slimRef, slimRect] = useRect({ scroll: true })
	

	// set products based on filters
	useEffect(() => {
		// Start with fresh set of variants each time
		let newVariants = products.nodes.map(product => product.content.variants).flat()
		filters.forEach(filter => {
			// Don't do anything if filter is set to all
			if(page.content?.filterOptions.find(option => filter.title === option.title).tags?.length === filter.tags.length) return
			// Otherwise filter
			newVariants = newVariants.filter(variant => {
				const filterMatch = variant.content.tags.filter(tag => filter.tags.includes(tag.title))
				console.log(variant.content.tags, filter.tags)
				return filterMatch.length > 0
			})
		})
		setVariants(order(newVariants))
	}, [filters])

	return(
		<>
			<Seo 
				title={title}
				metaTitle={content?.seo?.metaTitle}
				description={content?.seo?.metaDescription}
				image={content?.seo?.socialImage}
			/>
			<div>
				<Tabs>
					<AnimateSharedLayout>
						<AnimatedTabButton key="all" onClick={() => toPage({_type: 'productIndex'}, 0)} isSelected={currentTab === 0}>
							<h3 className={!isTablet ? 'h1' : ''}>All <Num>2</Num></h3>
						</AnimatedTabButton>
						{data?.nav?.indexTabs?.map((tab, i) => (
							<AnimatedTabButton 
								key={tab.slug} 
								onClick={() => toPage(tab, i + 1)}
								isSelected={currentTab === i + 1}
							>
								<h3 className={!isTablet ? 'h1' : ''}>{tab.title} <Num>2</Num></h3>
							</AnimatedTabButton>
						))}
					</AnimateSharedLayout>
				</Tabs>
				<div key={pageContext?.slug}>
					<motion.div 
						animate={controls}
						initial='hidden'
						transition={{ duration: 0.6 }}
						variants={{
							visible: { opacity: 1 },
							hidden: { opacity: 0 }
						}}
					>
						<Intro>
							<BlockContent blocks={page.content?.introduction} serializers={{
								types: {
									block: props => <h2 className="h1">{props.children}</h2>
								}
							}}/>
						</Intro>
						<SlimTabs ref={slimRef} css={css`
						border-color: ${slimRect?.top < 56 ? 'var(--black)' : 'transparent'};
						button, h3 {
							transition: opacity 0.3s;
							opacity: ${slimRect?.top < 56 ? 1 : 0};
						}
					`}>
							<h3>Category:</h3>
							<SlimButton key="allslim" onClick={() => toPage({_type: 'productIndex'}, 0, true)}>
								<h3 css={css`text-decoration: ${currentTab === 0 ? 'underline' : 'none'};`}>All</h3>
							</SlimButton>
							{data?.nav?.indexTabs?.map((tab, i) => (
								<SlimButton 
									key={tab.slug + 'slim'} 
									onClick={() => toPage(tab, i + 1, true)}
									css={css``}
								>
									<h3>
										<span css={css`text-decoration: ${currentTab === i + 1 ? 'underline' : 'none'};`}>
											{tab.title}
										</span> <Num>2</Num></h3>
								</SlimButton>
							))}
						</SlimTabs>
						<FilterWrap>
							<FilterBar css={css`border-bottom: ${isMobileFilter ? 'none' : '1px solid var(--black)'};`}>
								{isMobileFilter && 
						<NavHeading>
							<NavButton className='h3' onClick={()=> toggleSortNav()}>
									Filters
								<TopicsIcon css={css`transform: ${ navOpen ? 'rotate(45deg)': 'rotate(0deg)'};`}/>
							</NavButton>	
						</NavHeading>
								}
								{!isMobileFilter &&
						<>
							{page.content?.filterOptions?.map(option => (
								<NavSet key={option.title}>
									<h3>{option.title}:</h3>
									{option.tags?.length > 1 &&
									<TagLink 
										onClick={() => selectTag('all', option.title)} 
										css={css`
										text-decoration: ${filters.find(filter => filter.title === option.title).tags?.length === option.tags.length ? 'underline' : 'none' };
									`}
									>
										<h3>All</h3>
									</TagLink>
									}
									{option.tags?.map(tag => (
										<OptionSummaryButton key={tag} onClick={() => selectTag(tag.title, option.title)}>
											<VariantIcon image={tag.swatch} aspectRatio={1} />
											<h3 css={css`
												text-decoration: 
												${filters.find(filter => filter.title === option.title).tags[0] === tag.title
												&& filters.find(filter => filter.title === option.title).tags?.length < 2
											? 'underline' : 'none'};
											`}>{tag.title}</h3>
										</OptionSummaryButton>
									))}
								</NavSet>
							))}
						</>
								}
								<OrderBy className='h3' onClick={()=> toggleNavSort()} css={css`
							grid-column: 3/4;
							border-left: 1px solid var(--black);
							${mobile}{
								border-left: none;
							}
							margin-left: -1px;
						`}>
							Order By <DownArrow css={css`transform: ${ sortOpen ? 'rotate(180deg)': 'rotate(0deg)'};`}/>
								</OrderBy>
								{!isMobileFilter &&
							<SortDropDown css={css`
	
							`}>
								<SlideDown>
									{sortOpen &&
										<Sort>
											{sorts.map(sort => (
												<SortButton className='h3' key={sort} onClick={() => setCurrentSort(sort)} css={css`
													text-decoration: ${sort === currentSort ? 'underline' : 'none' };
												`}>
													{sort}
												</SortButton>
											))}
										</Sort>
									}
								</SlideDown>
							</SortDropDown>
								}
							</FilterBar>

							{isMobileFilter &&
						<div css={css`position: relative;`}>
							<MobileDropDown css={css`
								border-bottom: ${navOpen || sortOpen ? '1px solid var(--black)' : '1px solid transparent'};
								background: ${navOpen || sortOpen ? 'var(--beige)' : 'none'};
								transition-delay: ${navOpen || sortOpen ? '0s' : '0.46s'};
							`}>
								<SlideDown>
									{navOpen &&
										<MobileTagNav>
											{page.content?.filterOptions?.map((option, i) => (
												<MobileNavSet 
													key={option.title} 
													option={option} 
													selectTag={selectTag} 
													filters={filters}
													last={i + 1 === page.content?.filterOptions.length}
												/>
											))}
										</MobileTagNav>
									}
									{sortOpen &&
										<Sort>
											{sorts.map(sort => (
												<SortButton className='h3' key={sort} onClick={() => setCurrentSort(sort)} css={css`
												text-decoration: ${sort === currentSort ? 'underline' : 'none' };
											`}>
													{sort}
												</SortButton>
											))}
										</Sort>
									}
								</SlideDown>
							</MobileDropDown>
						</div>
							}
						</FilterWrap>
						<FadeTransition integer={filters.map(filter => filter.tags).flat().join('&') + currentSort}>
							<PageContent>
								{gridVisible ?
									<>
										{variants?.length > 0 ?
											<Grid>
												{variants?.map((variant, i) => 
													<ProductTile key={variant._key} product={variant} css={css`
												border-right: ${i % 3 === 2 ? 'none': '1px solid var(--black)'};
											`}/>
												)}
												<JNBox 
													css={css`
									grid-column: span ${3 - (variants?.length % 3)};
									display: ${(3 - (variants?.length % 3)) < 3 ? 'grid' : 'none'};
									${mobile}{
										display: none;
									}
							`}>
													<JNLogo/>
												</JNBox>
											</Grid>
											:
											<Section>
												<Message>
													<h2>No Products Found</h2>
												</Message>
											</Section>
										}
									</>
									:
									content?.flexibleContent?.map(module => (
										<RenderModule 
											key={module._key} 
											module={module}
										/>
									))
								}
							</PageContent>
						</FadeTransition>
					
					</motion.div>
				</div>
			</div>
			{/* <DataViewer data={vs} name="vs"/> */}
		</>
	)
}

const FilterWrap = styled.div`
	/* position: sticky;
	top: 111px;
	background: var(--beige);
	z-index: 10;
	${mobile}{
		top: 93px;
	} */
`

const Tabs = styled.header`
	display: flex;
	margin: var(--l) 0 var(--xxxl);
	border-bottom: 1px solid #C6C3B8;
	padding: 0 40px;
	${tablet}{
		margin: 3px 0 var(--xxxl);
	}
	${mobile}{
		justify-content: space-between;
		margin: 0;
		position: fixed;
		top: 46px;
		left: 0;
		right: 0;
		z-index: 20;
		background: var(--beige);
		padding: 0 20px;
	}
`

const SlimTabs = styled.div`
	position: sticky;
	display: flex;
	top: 55px;
	padding: var(--s) var(--m);
	z-index: 10;
	background: var(--beige);
	transition: opacity 0.3s;
	border-bottom: 1px solid;
	${mobile}{
		display: none;
	}
`

const SlimButton = styled.button`
	margin-left: var(--s);
`

const Intro = styled(Section)`
	margin-bottom: var(--xxxl);
	h2{
		grid-column: span 12;
		max-width: 850px;
		margin: 0 auto;
		text-align: center;
	}
	${tablet}{
		margin-top: calc(var(--xxxl) + 56px);
	}
`

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	${mobile}{
		grid-template-columns: 1fr;
	}
`

const ProductTile = styled(TileProduct)`
	border-bottom: 1px solid var(--black);
	${mobile}{
		border-right: none;
	}
`

const JNBox = styled.div`
	border-bottom: 1px solid var(--black);
	display: grid;
`
const JNLogo = styled(JNLetters)`
	height: 20px;
	align-self: center;
	justify-self: center;
`

const FilterBar = styled(NavBar)`
	border-top: 1px solid var(--black);
	grid-template-columns: 33.33333% 1fr max-content;
	background: var(--beige);
	@media(max-width: 1400px){
		grid-template-columns: max-content 1fr max-content;
	}
	/* display: flex; */
`

const PageContent = styled.div`
	
`

const NavSet = styled(NavLinks)`
	border-right: 1px solid var(--black);
	min-width: 30%;
	> h3{
		margin-right: 1em;
	}
	/* padding: var(--s) 0; */
`

const TagLink = styled(NavLink)`
	margin-right: var(--m);
`

const Message = styled.div`
	grid-column: span 12;
	padding: var(--xxxl) 0;
	text-align: center;
	${mobile}{
		grid-column: span 6;
	}
`

const MobileTagNav = styled(MobileNav)`
	display: block;
	padding: 0;
`

Category.propTypes = {
	data: PropTypes.object,
	pageContext: PropTypes.object,
}

const MobileNavSet = ({ className, option, filters, selectTag, last }) => {

	const [selection, setSelection] = useState()
	const [visible, setVisible] = useState()

	const apply = () => {
		setVisible(false)
		window.setTimeout(() => {
			if(selection){
				selectTag(selection, option.title)
			}
		}, 500)
	}

	useEffect(() => {
		if(!visible){
			setSelection(null)
		}
	}, [visible])

	return (
		<SetWrap key={option.title} className={className} css={css`
			border-bottom: ${last ? 'none' : '1px solid var(--black)'};
		`}>
			<SetHeader onClick={() => setVisible(!visible)} >
				<h3>
					{option.title}: {
						filters.find(filter => filter.title === option.title).tags?.length < 2 ? filters.find(filter => filter.title === option.title).tags[0] : 'All'
					}
					<TopicsIcon css={css`
						transform: ${ visible ? 'rotate(45deg)': 'rotate(0deg)'};
						margin-bottom: -1px;
					`}/>
				</h3>
			</SetHeader>
			<SlideDown>
				{visible &&
			<>
				{option.tags?.length > 1 &&
				<MobileTagButton 
					onClick={() => setSelection('all')} 
					css={css`
					text-decoration: ${(filters.find(filter => filter.title === option.title).tags?.length === option.tags.length && !selection ) || selection === 'all' ? 'underline' : 'none' };
					padding-top: var(--s);
				`}
				>
					<h3>All</h3>
				</MobileTagButton>
				}
				<MobileSetTags>
					{option.tags?.map(tag => (
						<MobileTagButton key={tag} onClick={() => setSelection(tag.title)}>
							<h3 css={css`
							text-decoration:
							${(
							filters.find(filter => filter.title === option.title).tags[0] === tag.title
							&& filters.find(filter => filter.title === option.title).tags?.length < 2
							&& !selection
						) || selection === tag.title
							? 'underline' : 'none'};
						`}>{tag.title}</h3>
						</MobileTagButton>
					))}
				</MobileSetTags>
				<ApplyButton onClick={() => apply()}>
					<h4>Apply &amp; Close <Tick ticked={true} /></h4>
				</ApplyButton>
			</>
				}
			</SlideDown>
			
		</SetWrap>
	)
}

const SetWrap = styled.div`
	
`

const SetHeader = styled.button`
	display: block;
	width: 100%;
	text-align: left;
	padding: var(--s) var(--m);
`

const MobileTagButton = styled.button`
	display: block;
	padding: var(--s) var(--m);
`

const MobileSetTags = styled.div`
	/* padding-bottom: var(--xs); */
`

const ApplyButton = styled.button`
	padding: var(--xs) var(--m) var(--m);
	svg{
		display: inline-block;
		width: 10px;
		transform: translateY(1px)
	}
`

MobileNavSet.propTypes = {
	option: PropTypes.object,
	filters: PropTypes.array,
	selectTag: PropTypes.func,
	className: PropTypes.string,
}

export const query = graphql`
  query CategoryQuery($slug: String, $isIndex: Boolean!) {
    indexPage: sanityProductIndex(slug: {current: {eq: $slug}}) @include(if: $isIndex) {
      title
      content {
        ...indexContent
      }
    }
		categoryPage: sanityProductCategory(slug: {current: {eq: $slug}}) @skip(if: $isIndex) {
      title
      content {
        ...indexContent
      }
    }
		allProducts: allSanityProduct @include(if: $isIndex) {
			nodes{
				content {
					variants {
						...variantThumb
					}
				}
			}
		}
		categoryProducts: allSanityProduct(
			filter: {content: {productCategory: {slug: {current: {eq: $slug}}}}}
			) @skip(if: $isIndex) {
			nodes{
				content {
					variants {
						...variantThumb
					}
				}
			}
		}
		nav: sanityNavigation {
			indexTabs {
				_type
				title
				slug {
					current
				}
			}
		}
  }
`

export default Category